// SPDX-License-Identifier: GPL-3.0-or-later
// A small typed synchronous bus for feedback events.
//
// - `emit` calls each listener at once, in the order of `subscribe`.
// - A listener that throws does not stop the others and does not reach the emitter. The error goes
//   to the optional `onError` hook.
// - The listener list is a snapshot array that only `subscribe` and `unsubscribe` replace. An emit
//   allocates nothing: it walks the array it holds. A listener that subscribes or unsubscribes
//   during an emit changes the next emit, not this one.
// - The bus has no clock, no DOM and no randomness.

import type { FeedbackEvent } from "./events.ts";

/** What a listener receives. */
export type FeedbackListener = (ev: FeedbackEvent) => void;

/** The part of the bus that an emitter needs. */
export interface FeedbackSink {
  emit(ev: FeedbackEvent): void;
}

/** The bus. */
export interface FeedbackBus extends FeedbackSink {
  /** Add a listener. The result removes it. Calling the result again does nothing. */
  subscribe(listener: FeedbackListener): () => void;
  /** The number of listeners now. */
  readonly size: number;
}

/** A sink that drops every event. It is the default when nobody listens. */
export const NULL_SINK: FeedbackSink = {
  emit(): void {
    // Nobody listens.
  },
};

/** Make a bus. `onError` gets the error of a listener that threw. It must not throw itself. */
export function createFeedbackBus(onError?: (error: unknown) => void): FeedbackBus {
  let listeners: readonly FeedbackListener[] = [];

  const report = (error: unknown): void => {
    try {
      onError?.(error);
    } catch {
      // The error hook must not break the game.
    }
  };

  return {
    get size() {
      return listeners.length;
    },
    subscribe(listener) {
      listeners = [...listeners, listener];
      let active = true;
      return () => {
        if (!active) return;
        active = false;
        const index = listeners.indexOf(listener);
        if (index >= 0) listeners = [...listeners.slice(0, index), ...listeners.slice(index + 1)];
      };
    },
    emit(ev) {
      const snapshot = listeners;
      for (let i = 0; i < snapshot.length; i++) {
        try {
          snapshot[i]?.(ev);
        } catch (error) {
          report(error);
        }
      }
    },
  };
}
